import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { bootstrapPublicBranding, ensureLoadingBlockPublished } from "./lib/branding/bootstrapPublicBranding";
import { FALLBACK_LOADING_BLOCK } from "./lib/branding/loadingBlockConstants";
import type { PublicBootstrapResult } from "./lib/branding/types";
import { PublicBrandingProvider, BrandingDomApplier } from "./contexts/PublicBrandingContext";
import { BootstrapSplash } from "./components/ui/bootstrap-splash";
import { traceLoading } from "./lib/traceLoading";
import { setBootstrapping, markBootstrapSplashHandoffComplete } from "./lib/appLoaderStore";
import { initSessionCoordinator } from "./lib/auth/sessionCoordinator";
import { initLoadingTransitionAuditSampler } from "./lib/loadingBootAudit";
import { initPreReactBootHandoff, releasePreReactBootSplashForAuthRoute } from "./lib/preReactBootHandoff";
import { isPreReactEarlyReleasePath } from "./lib/authPublicPaths";
// AG Grid init + CSS moved to ExcelAgGridViewer so grid chunk loads only with WorkReports/UniversalPreviewPage (not main)

// DEV-only: network audit (intercept fetch, aggregate by route/endpoint, copyNetworkAuditToClipboard())
if (import.meta.env.DEV) {
  import('@/dev/networkAudit').then((m) => m.installNetworkAudit());
}

initSessionCoordinator();
ensureLoadingBlockPublished();
initPreReactBootHandoff();
initLoadingTransitionAuditSampler();

/**
 * Bootstrap Root - Blocking bootstrap before rendering App
 * 
 * Flow:
 * 1. Fetch public branding (blocking)
 * 2. Initialize PublicBrandingProvider with normalized branding
 * 3. Render App with BrandingDomApplier to apply to DOM
 */
const BootstrapRoot: React.FC = () => {
  const [bootstrap, setBootstrap] = React.useState<PublicBootstrapResult | null>(null);
  const [isBootstraping, setIsBootstraping] = React.useState(true);

  if (import.meta.env.DEV) traceLoading('isBootstraping', isBootstraping, {});

  React.useEffect(() => {
    setBootstrapping(true, 'bootstrap_root');
    const BOOTSTRAP_TIMEOUT_MS = 8_000;
    const fallbackBootstrap: PublicBootstrapResult = {
      branding: {
        site_title: null,
        logo_src: null,
        favicon_src: null,
        loading_src: null,
        og_title: null,
        og_description: null,
        og_image_src: null,
        version: '0',
      },
      publicUi: {
        loading_animation_type: FALLBACK_LOADING_BLOCK.loadingType,
        loader_indicator_duration_ms: FALLBACK_LOADING_BLOCK.loadingSpeedMs,
        enable_animations: FALLBACK_LOADING_BLOCK.animationsEnabled,
      },
    };

    let settled = false;
    const finish = (result: PublicBootstrapResult) => {
      if (settled) return;
      settled = true;
      markBootstrapSplashHandoffComplete();
      setBootstrapping(false, 'bootstrap_root');
      if (isPreReactEarlyReleasePath(window.location.pathname)) {
        releasePreReactBootSplashForAuthRoute('bootstrap-finish-early-release');
      }
      setBootstrap(result);
      setIsBootstraping(false);
    };

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      console.warn('[BootstrapRoot] Bootstrap timeout, using fallback branding');
      finish(fallbackBootstrap);
    }, BOOTSTRAP_TIMEOUT_MS);

    bootstrapPublicBranding()
      .then((result) => finish(result))
      .catch((error) => {
        console.error('[BootstrapRoot] Bootstrap failed:', error);
        finish(fallbackBootstrap);
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
      });
  }, []);

  if (isBootstraping || !bootstrap) {
    return <BootstrapSplash />;
  }

  // Render App with branding provider
  return (
    <PublicBrandingProvider initial={bootstrap.branding}>
      <BrandingDomApplier />
      <App />
    </PublicBrandingProvider>
  );
};

// Render BootstrapRoot (which handles blocking bootstrap)
createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BootstrapRoot />
  </React.StrictMode>
);
